/**
 * The ONE role ladder. `OrgRole` is gone — see
 * `docs/plans/2026-09-08-workspace-rbac-and-plan-restrictions.md`.
 *
 * Every role is stored per-workspace in `workspaceMembers.role`. `OWNER` is the
 * exception in MEANING but not in STORAGE: it is a company-wide capability
 * (every workspace, plus billing / org settings / workspace create-delete), yet
 * it is still written as a real row in EVERY workspace of the org. There is no
 * implicit short-circuit any more — an owner who holds no row for a workspace
 * does not have access to it, and `WorkspaceService.createWorkspace` writes the
 * owner rows synchronously so that can never happen.
 *
 * Why storage is denormalized on purpose: the previous model kept ownership in a
 * SECOND enum (`organizationMembers.role`) that two independent endpoints could
 * move apart, and 24 live users ended up in combinations neither ladder could
 * express. One ladder cannot disagree with itself.
 *
 * The invariant that replaces it: OWNER is all-or-nothing. A user is an owner in
 * every workspace of their org or in none. `isOwnerRow`-style partial states are
 * a bug — `SettingsService.demoteOwner` and `leaveWorkspace` are the only two
 * writers allowed to move a user off OWNER, and both rewrite every row.
 */
export enum WorkspaceRole {
  OWNER = 'owner',
  ADMIN = 'admin',
  EDITOR = 'editor',
  VIEWER = 'viewer',
}

/**
 * Rank for "at least this role" checks. Higher wins. Exported because the guard,
 * the settings service and the audit actor-role helper all need the same order —
 * three private copies is how they drift.
 */
export const WORKSPACE_ROLE_RANK = {
  [WorkspaceRole.OWNER]: 4,
  [WorkspaceRole.ADMIN]: 3,
  [WorkspaceRole.EDITOR]: 2,
  [WorkspaceRole.VIEWER]: 1,
} as const satisfies Record<WorkspaceRole, number>;

export enum SocialProvider {
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
}

/**
 * How an account was ORIGINALLY created. Set once at signup and never
 * overwritten — that immutability is the whole point, and is the one place this
 * deliberately diverges from the previous TrulyInbox, whose equivalent setting
 * was rewritten on every OAuth login (`updateAuthenticationChannel`) and so
 * recorded the latest channel rather than the origin. A value that moves cannot
 * gate anything.
 *
 * It decides which sign-in paths an account may use:
 *   - `EMAIL_PASSWORD` -> password login, and (once `emailVerified`) magic link
 *     and OAuth too.
 *   - everything else  -> password login is refused forever, and so is a
 *     password reset: issuing one would hand a magic-link/OAuth account a
 *     password and quietly undo the restriction.
 */
export enum AuthMethod {
  EMAIL_PASSWORD = 'email_password',
  MAGIC_LINK = 'magic_link',
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
}

/** The social providers, as `AuthMethod` values. */
export const SOCIAL_PROVIDER_TO_AUTH_METHOD = {
  [SocialProvider.GOOGLE]: AuthMethod.GOOGLE,
  [SocialProvider.MICROSOFT]: AuthMethod.MICROSOFT,
} as const satisfies Record<SocialProvider, AuthMethod>;

/**
 * Methods that must NEVER be able to reach the email+password paths. Derived
 * from the enum rather than listed, so a new method is excluded by default —
 * the safe direction. Adding a method that SHOULD have password access means
 * changing `canUsePasswordAuth`, which is one place, not a list to remember.
 */
export function canUsePasswordAuth(method: AuthMethod | null | undefined): boolean {
  return method === AuthMethod.EMAIL_PASSWORD;
}
